import { useCallback, useRef, useState } from "react";

export interface SelectionItem {
  rowId: string;
  cellKey: number;
}

export interface UseDragSelectionOptions {
  /** Filter function to determine if an item is selectable */
  isSelectable?: (rowId: string, cellKey: number) => boolean;
}

export interface UseDragSelectionReturn {
  /** Currently selected items as "rowId:cellKey" strings */
  selectedItems: Set<string>;
  /** Whether the bulk input should be shown */
  showBulkInput: boolean;
  /** Current editing value for bulk input */
  editingValue: string;
  /** Set the editing value */
  setEditingValue: (value: string) => void;
  /** Check if an item is selected */
  isSelected: (rowId: string, cellKey: number) => boolean;
  /** Handle pointer down on a cell */
  handlePointerDown: (e: React.PointerEvent, rowId: string, cellKey: number) => void;
  /** Handle pointer move (attach to container) */
  handlePointerMove: (e: React.PointerEvent) => void;
  /** Handle pointer up (attach to container) */
  handlePointerUp: (e: React.PointerEvent) => void;
  /** Commit the bulk input value */
  commitBulkInput: () => Array<{ rowId: string; cellKey: number; value: number }> | null;
  /** Cancel the selection */
  cancelSelection: () => void;
  /** Register a row's grid element for cross-row drag detection */
  registerRowGrid: (rowId: string, element: HTMLDivElement | null, cellKeys: number[]) => void;
  /** Last single-clicked cell (set when a cell is clicked without dragging) */
  lastSingleCellClick: { rowId: string; cellKey: number; singleCellOnly: boolean } | null;
  /** Clear the last single cell click */
  clearLastSingleCellClick: () => void;
}

interface RowGridInfo {
  element: HTMLDivElement;
  cellKeys: number[];
}

function itemKey(rowId: string, cellKey: number): string {
  return `${rowId}:${cellKey}`;
}

function parseItemKey(key: string): { rowId: string; cellKey: number } | null {
  const colonIndex = key.lastIndexOf(":");
  if (colonIndex === -1) return null;
  const rowId = key.slice(0, colonIndex);
  const cellKey = parseInt(key.slice(colonIndex + 1), 10);
  if (isNaN(cellKey)) return null;
  return { rowId, cellKey };
}

/**
 * Hook for rectangular drag selection across grid cells.
 * Enables bulk hour editing by selecting multiple cells across rows.
 * 
 * Usage:
 * 1. Wrap your grid container with DragSelectionProvider
 * 2. Call registerRowGrid for each row to enable cross-row selection
 * 3. Attach handlePointerDown to each selectable cell
 * 4. Attach handlePointerMove and handlePointerUp to the container
 * 5. Use showBulkInput to display the bulk editing UI
 * 6. Call commitBulkInput when the user confirms the value
 */
export function useDragSelection(options: UseDragSelectionOptions = {}): UseDragSelectionReturn {
  const { isSelectable = () => true } = options;

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [showBulkInput, setShowBulkInput] = useState(false);
  const [editingValue, setEditingValue] = useState("");
  const [lastSingleCellClick, setLastSingleCellClick] = useState<{ rowId: string; cellKey: number; singleCellOnly: boolean } | null>(null);

  const dragStartItem = useRef<string | null>(null);
  const baseSelection = useRef<Set<string>>(new Set());
  const rowGrids = useRef<Map<string, RowGridInfo>>(new Map());
  const didDragMove = useRef(false);
  const capturedElement = useRef<{ element: Element; pointerId: number } | null>(null);
  // Whether the single-cell-edit modifier (Cmd/Ctrl) was held at pointer down.
  const pointerDownModifierKey = useRef(false);

  const registerRowGrid = useCallback((rowId: string, element: HTMLDivElement | null, cellKeys: number[]) => {
    if (element) {
      rowGrids.current.set(rowId, { element, cellKeys });
    } else {
      rowGrids.current.delete(rowId);
    }
  }, []);

  const getItemFromPointer = useCallback((clientX: number, clientY: number): { rowId: string; cellKey: number } | null => {
    for (const [rowId, info] of rowGrids.current) {
      const rect = info.element.getBoundingClientRect();
      if (clientY >= rect.top && clientY <= rect.bottom) {
        const cellWidth = 64;
        const relativeX = clientX - rect.left + info.element.scrollLeft;
        const cellIndex = Math.floor(relativeX / cellWidth);
        if (cellIndex >= 0 && cellIndex < info.cellKeys.length) {
          const cellKey = info.cellKeys[cellIndex];
          if (cellKey !== undefined) {
            return { rowId, cellKey };
          }
        }
      }
    }
    return null;
  }, []);

  const updateRangeSelection = useCallback((endItem: { rowId: string; cellKey: number }) => {
    if (!dragStartItem.current) return;

    const startParsed = parseItemKey(dragStartItem.current);
    if (!startParsed) return;

    const newSelection = new Set(baseSelection.current);
    
    // Get all row IDs in order based on DOM position
    const rowEntries = Array.from(rowGrids.current.entries());
    rowEntries.sort((a, b) => {
      const rectA = a[1].element.getBoundingClientRect();
      const rectB = b[1].element.getBoundingClientRect();
      return rectA.top - rectB.top;
    });

    const rowIds = rowEntries.map(([id]) => id);
    const startRowIdx = rowIds.indexOf(startParsed.rowId);
    const endRowIdx = rowIds.indexOf(endItem.rowId);

    if (startRowIdx === -1 || endRowIdx === -1) return;

    const [minRowIdx, maxRowIdx] = startRowIdx <= endRowIdx 
      ? [startRowIdx, endRowIdx] 
      : [endRowIdx, startRowIdx];

    // Get the column indices from the start row to use as reference
    const startRowInfo = rowGrids.current.get(startParsed.rowId);
    const endRowInfo = rowGrids.current.get(endItem.rowId);
    if (!startRowInfo || !endRowInfo) return;

    // Find the column indices (not cell keys) for rectangular selection
    const startColIdx = startRowInfo.cellKeys.indexOf(startParsed.cellKey);
    const endColIdx = endRowInfo.cellKeys.indexOf(endItem.cellKey);
    
    if (startColIdx === -1 || endColIdx === -1) return;

    const [minColIdx, maxColIdx] = startColIdx <= endColIdx 
      ? [startColIdx, endColIdx] 
      : [endColIdx, startColIdx];

    // For each row in range, select cells at the same column indices (rectangular selection)
    for (let rowIdx = minRowIdx; rowIdx <= maxRowIdx; rowIdx++) {
      const rowId = rowIds[rowIdx];
      if (!rowId) continue;
      
      const rowInfo = rowGrids.current.get(rowId);
      if (!rowInfo) continue;

      // Select cells at the same column indices across all rows
      for (let colIdx = minColIdx; colIdx <= maxColIdx; colIdx++) {
        if (colIdx >= 0 && colIdx < rowInfo.cellKeys.length) {
          const cellKey = rowInfo.cellKeys[colIdx];
          if (cellKey !== undefined && isSelectable(rowId, cellKey)) {
            newSelection.add(itemKey(rowId, cellKey));
          }
        }
      }
    }

    setSelectedItems(newSelection);
  }, [isSelectable]);

  const handlePointerDown = useCallback((e: React.PointerEvent, rowId: string, cellKey: number) => {
    if (!isSelectable(rowId, cellKey)) return;

    const key = itemKey(rowId, cellKey);

    // Cmd/Ctrl held marks a single-cell edit (only the clicked cell), as
    // opposed to the default fill-forward edit. Capture it for use on a
    // single-cell click (no drag) in handlePointerUp.
    pointerDownModifierKey.current = e.metaKey || e.ctrlKey;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    capturedElement.current = { element: target, pointerId: e.pointerId };
    setIsDragging(true);
    dragStartItem.current = key;
    baseSelection.current = new Set();
    setSelectedItems(new Set([key]));
    setShowBulkInput(false);
    didDragMove.current = false;
  }, [isSelectable]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    const currentItem = getItemFromPointer(e.clientX, e.clientY);
    if (currentItem) {
      const currentKey = itemKey(currentItem.rowId, currentItem.cellKey);
      if (dragStartItem.current && currentKey !== dragStartItem.current) {
        didDragMove.current = true;
      }
      updateRangeSelection(currentItem);
    }
  }, [isDragging, getItemFromPointer, updateRangeSelection]);

  const handlePointerUp = useCallback((_e: React.PointerEvent) => {
    if (!isDragging) return;

    // Release pointer capture on the same element that captured it
    if (capturedElement.current) {
      try {
        capturedElement.current.element.releasePointerCapture(capturedElement.current.pointerId);
      } catch {
        // Ignore errors if capture was already released
      }
      capturedElement.current = null;
    }
    setIsDragging(false);
    dragStartItem.current = null;

    // Only show bulk input for multi-cell selection (2+ cells)
    if (selectedItems.size > 1) {
      setShowBulkInput(true);
      // Default to empty - user enters hours value
      setEditingValue("");
    } else if (selectedItems.size === 1 && !didDragMove.current) {
      // Single cell click (no drag) - set lastSingleCellClick for inline editing
      const firstKey = selectedItems.values().next().value;
      if (firstKey) {
        const parsed = parseItemKey(firstKey);
        if (parsed) {
          setLastSingleCellClick({ ...parsed, singleCellOnly: pointerDownModifierKey.current });
        }
      }
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set());
    }
  }, [isDragging, selectedItems]);

  const isSelected = useCallback((rowId: string, cellKey: number): boolean => {
    return selectedItems.has(itemKey(rowId, cellKey));
  }, [selectedItems]);

  const commitBulkInput = useCallback((): Array<{ rowId: string; cellKey: number; value: number }> | null => {
    const numValue = parseFloat(editingValue);
    if (isNaN(numValue) || numValue < 0 || selectedItems.size === 0) {
      return null;
    }

    const updates: Array<{ rowId: string; cellKey: number; value: number }> = [];
    for (const key of selectedItems) {
      const parsed = parseItemKey(key);
      if (parsed) {
        updates.push({ ...parsed, value: numValue });
      }
    }

    setShowBulkInput(false);
    setSelectedItems(new Set());
    setEditingValue("");

    return updates;
  }, [editingValue, selectedItems]);

  const cancelSelection = useCallback(() => {
    setShowBulkInput(false);
    setSelectedItems(new Set());
    setEditingValue("");
    dragStartItem.current = null;
  }, []);

  const clearLastSingleCellClick = useCallback(() => {
    setLastSingleCellClick(null);
  }, []);

  return {
    selectedItems,
    showBulkInput,
    editingValue,
    setEditingValue,
    isSelected,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    commitBulkInput,
    cancelSelection,
    registerRowGrid,
    lastSingleCellClick,
    clearLastSingleCellClick,
  };
}
